import {
  BodyText,
  GridWithGaps,
  HeadingH2,
  HeadingH4,
  Text_16_24,
  useYupValidationResolver,
} from '@dreamstack/feature-components'
import { useFormatToCurrencyCutoff } from '@dreamstack/i18n'
import { Checkmark, Clear } from '@dreamstack/icons'
import { SimpleInput, SimpleRadio } from '@dreamstack/simple-components'
import type { FunctionComponent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import tw, { styled } from 'twin.macro'
import * as yup from 'yup'
import { LtvInfoIcon } from './LtvInfoIcon'

type LtvFormData = {
  buyer: 'international' | 'local'
  propertyPrice: number
  investment: number
}

const MeterDivider = styled.div<{ transparent?: boolean }>(
  ({ transparent }) => [
    tw`h-full width[2px] relative`,
    transparent ? tw`bg-transparent` : tw`bg-accentroAquaVeryLight-full`,
  ]
)

const PercentageLabelWrapper = styled.p<{ firstOrLast?: boolean }>(
  ({ firstOrLast }) => [
    tw`pt-px-16 text-px-13 leading-px-18 -translate-x-1/2 translate-y-1/2 absolute -bottom-full`,
    firstOrLast ? tw`block` : tw`hidden md:block`,
  ]
)

const BubbleWrapper = styled.div<{ percentage?: number }>(({ percentage }) => [
  `margin-left: ${percentage}%`,
])
const Bubble = tw.div`mt-px-24 bg-accentroGray-full text-white relative rounded-full flex 
flex-col justify-center items-center h-px-80 w-px-80 -translate-x-1/2 padding[10px] md:(h-px-196 w-px-196)`
const BubbleTriangle = tw.div`absolute -bottom-px-4 rotate-45 left-1/2 -translate-x-1/2 bg-accentroGray-full h-px-12 w-px-12 md:(h-px-48 w-px-48)`
const BubbleSmallText = tw.p`text-center font-semibold font-size[8px] line-height[12px] md:(text-px-13 leading-px-18)`
const BubbleNumber = tw.p`text-center font-semibold text-px-28 leading-px-36 md:(text-px-72 leading-px-80)`
const BubbleSymbol = tw.span`font-semibold font-size[11px] line-height[16.5px] md:(text-px-28 leading-px-42)`
const Meter = styled.div<{ internationalBuyer?: boolean }>(
  ({ internationalBuyer }) => [
    tw`h-px-16 flex justify-between w-full mt-px-20 md:(mt-px-32)`,
    internationalBuyer
      ? `background: linear-gradient(to right, #73c49f 67%, #e94e25 72%, #e94e25)`
      : `background: linear-gradient(to right, #73c49f 90%, #e94e25 95%, #e94e25)`,
  ]
)

const CheckmarkCircle = styled.div<{ ltvIsAccepted?: boolean }>(
  ({ ltvIsAccepted }) => [
    ltvIsAccepted
      ? tw`border-accentroLightGreen-full`
      : tw`border-accentroError-full`,
    tw`border-width[3px] flex justify-center items-center rounded-full h-px-48 w-px-48 flex-shrink-0 mr-px-20 md:(h-px-80 w-px-80 mr-px-24)`,
  ]
)

const schema = {
  propertyPrice: yup.number(),
  investment: yup.number(),
}

const calcualteLtv = (
  banksPropertyValuation: number,
  mortgageAmount: number
): number => {
  const ltv = Math.round((mortgageAmount / banksPropertyValuation) * 100)
  const boundariedLtv = ltv < 0 ? 0 : ltv > 100 ? 100 : ltv
  return boundariedLtv
}

export const LtvCalculator: FunctionComponent<
  React.PropsWithChildren<{}>
> = () => {
  const yupSchemaResolver = useYupValidationResolver(yup.object(schema))
  const methods = useForm<{
    buyer: string
    propertyPrice: number
    investment: number
  }>({
    resolver: yupSchemaResolver,
  })
  const { control, register, watch } = methods
  const { buyer, propertyPrice, investment } = watch()

  const [ltv, setLtv] = useState(67)
  const [banksPropertyValuation, setBanksPropertyValuation] = useState(0)
  const [mortgageAmount, setMortgageAmount] = useState(0)

  const format = useFormatToCurrencyCutoff()

  useEffect(() => {
    const banksPropertyValuation = propertyPrice * 0.9
    const mortgageAmount = propertyPrice - investment
    const ltv = calcualteLtv(banksPropertyValuation, mortgageAmount)
    setBanksPropertyValuation(banksPropertyValuation)
    setMortgageAmount(mortgageAmount > 0 ? mortgageAmount : 0)
    setLtv(ltv)
  }, [buyer, propertyPrice, investment])

  const meterDivider = useMemo(() => {
    return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((e, i) => {
      const percentageLabel = `${e * 10}%`
      const firstOrLast = [0, 10].includes(e)
      return (
        <MeterDivider transparent={firstOrLast} key={i}>
          <PercentageLabelWrapper firstOrLast={firstOrLast}>
            {percentageLabel}
          </PercentageLabelWrapper>
        </MeterDivider>
      )
    })
  }, [])

  const local = buyer === 'local'
  const ltvCliff = local ? 90 : 67
  const ltvIsAccepted = ltv <= ltvCliff
  const aboveBelow = ltvIsAccepted ? 'below' : 'above'
  const increaseDecrease = ltvIsAccepted ? 'increases' : 'decreases'
  const ltvInfoText = `Your LTV is ${aboveBelow} the LTV Cliff of ${ltvCliff}%, which ${increaseDecrease} the likelihood of getting approved by a German bank.`

  const { ref, ...registerBuyer } = register('buyer')
  return (
    <GridWithGaps>
      <HeadingH2 tw="col-span-full">Calculate your individual LTV</HeadingH2>
      <BodyText tw="col-span-full mt-px-24">
        When purchasing a property with a mortgage, you will often hear the term
        LTV, meaning Loan to Value. This is the percentage of the property price
        that will be the mortgage, which is calculated by how much money you can
        put into the investment. You can work out your personal LTV using the
        tool below:
      </BodyText>
      <FormProvider {...methods}>
        <form tw="col-span-full">
          <div tw="mt-px-32 md:mt-px-48">
            <SimpleRadio
              ref={ref}
              radioGroupProps={{
                ...registerBuyer,
                defaultValue: 'international',
                row: true,
              }}
              options={[
                { label: 'International Buyer', value: 'international' },
                { label: 'Local Buyer', value: 'local' },
              ]}
            />
          </div>
          <div tw="mt-px-40">
            <div tw="flex flex-col md:(flex-row gap-x-px-48) ml:(gap-x-px-32)">
              <div tw="space-y-px-16 md:space-y-px-24">
                <Controller
                  defaultValue={400_000}
                  name="propertyPrice"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={'Property Price'}
                      InputProps={{
                        endAdornment: 'EUR',
                        inputProps: { inputMode: 'numeric' },
                      }}
                    />
                  )}
                />
                <Controller
                  defaultValue={160_000}
                  name="investment"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={'How much can you invest?'}
                      InputProps={{
                        endAdornment: 'EUR',
                        inputProps: { inputMode: 'numeric' },
                      }}
                    />
                  )}
                />
              </div>
              <div tw="space-y-px-16 mt-px-24 md:(space-y-px-24 mt-0)">
                <div>
                  <div tw="flex flex-row justify-start items-center space-x-px-4 -mt-px-8">
                    <Text_16_24>Bank’s property valuation</Text_16_24>
                    <LtvInfoIcon />
                  </div>
                  <p tw="-mt-px-12 text-px-16 leading-px-24 md:(text-px-24 leading-px-36) font-semibold">
                    {format(banksPropertyValuation)} EUR
                  </p>
                </div>
                <div tw="">
                  <p tw="text-px-16 leading-px-24 md:(text-px-20 leading-px-30)">
                    Mortgage amount
                  </p>
                  <p tw="text-px-24 leading-px-36 md:(text-px-40 leading-px-50) font-semibold">
                    {format(mortgageAmount)}{' '}
                    <span tw="text-px-16 leading-px-24 md:(text-px-28 leading-px-42)">
                      EUR
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
      <div tw="col-span-full">
        <BubbleWrapper percentage={ltv}>
          <Bubble>
            <BubbleSmallText>Your individual LTV</BubbleSmallText>
            <BubbleNumber>
              {ltv}
              <BubbleSymbol>%</BubbleSymbol>
            </BubbleNumber>
            <BubbleTriangle />
          </Bubble>
        </BubbleWrapper>
        <Meter internationalBuyer={buyer === 'international'}>
          {meterDivider}
        </Meter>
      </div>
      <div tw="col-span-full flex items-start mt-px-64 md:mt-px-80">
        <CheckmarkCircle ltvIsAccepted={ltvIsAccepted}>
          {ltvIsAccepted ? (
            <Checkmark tw="text-accentroLightGreen-full md:(h-px-40 w-px-40)" />
          ) : (
            <Clear tw="text-accentroError-full md:(h-px-40 w-px-40)" />
          )}
        </CheckmarkCircle>
        <div>
          <HeadingH4 tw="font-semibold">{ltvInfoText} </HeadingH4>
          <p tw="text-px-13 leading-px-18 text-accentroGray-500 mt-px-32">
            Loan-to-value = (loan amount after deduction of equity x 100) /
            mortgage lending value.
          </p>
        </div>
      </div>
    </GridWithGaps>
  )
}
